/**
 * Filter Metadata Generator - generates filter metadata from files
 * 
 * Computes unique values and cross-dimension availability maps
 */

/**
 * FilterMetadataGenerator Class - Generates filter metadata
 */
class FilterMetadataGenerator {
    /**
     * Compute min/max date from files (uses 'd' field, expects YYYY-MM-DD)
     * @param {Array} files - Files array
     * @returns {Object} - Date bounds {min, max}
     */
    computeDateBounds(files) {
        try {
            let min = null, max = null;
            for (const f of files || []) {
                const d = (f && typeof f.d === 'string' && /\d{4}-\d{2}-\d{2}/.test(f.d)) ? f.d : null;
                if (!d) continue;
                if (!min || d < min) min = d;
                if (!max || d > max) max = d;
            }
            return { min, max };
        } catch(_) { return { min: null, max: null }; }
    }
    
    /**
     * Generate filter metadata from files array
     * Returns: { 
     *   uniqueValues: {L: [], F: [], M: [], S: []},
     *   availabilityMap: {L: Set, F: Set, M: Set, S: Set} per value
     * }
     * 
     * Structure optimized for fast disabled computation:
     * - uniqueValues: all possible values per dimension
     * - availabilityMap: for each value, tracks which other dimension values are available
     *   Format: { L: { 'EN': Set(['AUDIO', 'TEXT']), ... }, F: { 'AUDIO': Set(['EN', 'RU']), ... }, ... }
     * @param {Array} files - Files array
     * @returns {Object} - Filter metadata
     */
    generateFilterMetadata(files) {
        if (!Array.isArray(files) || files.length === 0) {
            return {
                uniqueValues: { L: [], F: [], M: [], S: [] },
                mgGroups: {},
                availabilityMap: { L: {}, F: {}, M: {}, S: {} }
            };
        }
        
        const uniqueValues = { L: new Set(), F: new Set(), M: new Set(), S: new Set() };
        const mgGroups = {};
        const availabilityMap = { L: {}, F: {}, M: {}, S: {} };
        
        for (const file of files) {
            if (!file || typeof file !== 'object') continue;
            
            const l = file.L || 'empty';
            const f = file.F || 'empty';
            const m = file.M || 'empty';
            const s = file.S || 'empty';
            const mg = file.MG || 'empty';
            
            // Collect unique values
            uniqueValues.L.add(l);
            uniqueValues.F.add(f);
            uniqueValues.M.add(m);
            uniqueValues.S.add(s);
            
            // Group M values by MG
            if (!mgGroups[mg]) mgGroups[mg] = new Set();
            mgGroups[mg].add(m);
            
            // Track cross-dimension availability
            // Initialize maps if needed
            if (!availabilityMap.L[l]) availabilityMap.L[l] = { F: new Set(), M: new Set(), S: new Set() };
            if (!availabilityMap.F[f]) availabilityMap.F[f] = { L: new Set(), M: new Set(), S: new Set() };
            if (!availabilityMap.M[m]) availabilityMap.M[m] = { L: new Set(), F: new Set(), S: new Set() };
            if (!availabilityMap.S[s]) availabilityMap.S[s] = { L: new Set(), F: new Set(), M: new Set() };
            
            // Record co-occurrences
            availabilityMap.L[l].F.add(f);
            availabilityMap.L[l].M.add(m);
            availabilityMap.L[l].S.add(s);
            
            availabilityMap.F[f].L.add(l);
            availabilityMap.F[f].M.add(m);
            availabilityMap.F[f].S.add(s);
            
            availabilityMap.M[m].L.add(l);
            availabilityMap.M[m].F.add(f);
            availabilityMap.M[m].S.add(s);
            
            availabilityMap.S[s].L.add(l);
            availabilityMap.S[s].F.add(f);
            availabilityMap.S[s].M.add(m);
        }
        
        // Convert Sets to Arrays for JSON serialization
        const convertAvailabilityMap = (map) => {
            const result = {};
            for (const [key, dims] of Object.entries(map)) {
                if (!dims || typeof dims !== 'object') continue;
                result[key] = {};
                for (const [dim, vals] of Object.entries(dims)) {
                    if (vals && vals instanceof Set) {
                        result[key][dim] = Array.from(vals);
                    }
                }
            }
            return result;
        };
        
        // Convert mgGroups Sets to Arrays and filter out 'empty' M values
        const mgGroupsArray = {};
        for (const [mg, mSet] of Object.entries(mgGroups)) {
            // Filter out 'empty' from M values in each group
            const filteredMValues = Array.from(mSet).filter(m => m !== 'empty');
            if (filteredMValues.length > 0) {
                mgGroupsArray[mg] = filteredMValues;
            }
        }
        
        // Filter out 'empty' values from uniqueValues if they're the only value
        const filterEmptyValues = (values) => {
            const arr = Array.from(values);
            if (arr.length === 1 && arr[0] === 'empty') return [];
            if (arr.every(v => v === 'empty')) return [];
            return arr.filter(v => v !== 'empty');
        };
        
        return {
            uniqueValues: {
                L: filterEmptyValues(uniqueValues.L),
                F: filterEmptyValues(uniqueValues.F),
                M: filterEmptyValues(uniqueValues.M),
                S: filterEmptyValues(uniqueValues.S)
            },
            mgGroups: mgGroupsArray,
            availabilityMap: {
                L: convertAvailabilityMap(availabilityMap.L),
                F: convertAvailabilityMap(availabilityMap.F),
                M: convertAvailabilityMap(availabilityMap.M),
                S: convertAvailabilityMap(availabilityMap.S)
            }
        };
    }
    
    /**
     * Save filter metadata to IndexedDB meta store
     * @param {IDBDatabase} db - IndexedDB database instance
     * @param {Object} filterMeta - Filter metadata to save
     * @returns {Promise<boolean>} - Success status
     */
    async saveFilterMetadata(db, filterMeta) {
        return new Promise((resolve, reject) => {
            try {
                const tx = db.transaction('meta', 'readwrite');
                const store = tx.objectStore('meta');
                const value = {
                    key: 'filter_metadata',
                    ...filterMeta,
                    updatedAt: new Date().toISOString()
                };
                const req = store.put(value);
                req.onsuccess = () => resolve(true);
                req.onerror = () => reject(req.error || new Error('Failed to write filter metadata'));
            } catch (e) {
                reject(e);
            }
        });
    }
}

export { FilterMetadataGenerator };

